import { useCallback, useLayoutEffect, useState, type RefCallback } from 'react';

/**
 * True when the element's content overflows its box (i.e. it can scroll).
 * Returns a callback ref + the overflowing flag.
 * Used for conditional modal header dividers: show the line only when the body scrolls.
 */
export function useIsOverflowing(enabled: boolean = true): [RefCallback<HTMLElement>, boolean] {
    const [node, setNode] = useState<HTMLElement | null>(null);
    const [overflowing, setOverflowing] = useState(false);

    const ref = useCallback<RefCallback<HTMLElement>>((el) => {
        setNode(el);
    }, []);

    useLayoutEffect(() => {
        if (!enabled || !node) {
            setOverflowing(false);
            return;
        }

        const measure = () => {
            // Prefer the actual scrollport if this node isn't scrolling but a child is.
            let el: HTMLElement | null = node;
            if (el.scrollHeight <= el.clientHeight + 1) {
                const child = el.querySelector<HTMLElement>('[data-overflow-root], .chakra-modal__body');
                if (child && child !== el) el = child;
            }
            setOverflowing(el.scrollHeight > el.clientHeight + 1);
        };

        measure();
        // Modal enter animation / max-height apply a frame or two later.
        const raf1 = requestAnimationFrame(() => {
            measure();
            requestAnimationFrame(measure);
        });
        const t = window.setTimeout(measure, 50);

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(node);
        if (node.parentElement) resizeObserver.observe(node.parentElement);

        const mutationObserver = new MutationObserver(measure);
        mutationObserver.observe(node, { childList: true, subtree: true, characterData: true });

        return () => {
            cancelAnimationFrame(raf1);
            window.clearTimeout(t);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [node, enabled]);

    return [ref, overflowing];
}
